// @flow
import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';
import { messageFormat } from '@clubmed/utils';

import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { ICONS } from 'Components/utils/icon/icons';
import { TripDatePicker } from 'Components/widgets/trip-date-picker/TripDatePicker';
import { dateDiff } from 'Components/widgets/trip-date-picker/TripDatePicker.utils';
import { t } from 'Src/i18n';
import { type PricesByDays } from 'Store/calendar/calendar.helper';

export type Props = {
  changeDepartureDate: (?Date) => mixed,
  changeTripDuration: (?number) => mixed,
  dateFormat: string,
  defaultDuration?: ?number,
  departureDate: ?number | ?moment$Moment,
  duration: ?number,
  isSubmittable?: boolean,
  label?: string,
  navigationByYear?: boolean,
  openCalendarToDate?: ?number | ?moment$Moment,
  optionsDuration?: Array<number>,
  prices?: PricesByDays,
  showDurationLabel?: boolean,
  offsetCalendar?: ?number,
};

const PriceLegend = () => {
  return (
    <div className="content-start flex flex-wrap justify-center">
      <div className="text-center text-sm pl-2 py-1 text-emerald">
        {t('faceting.when.bestPrice')}
      </div>
      <div className="text-center text-sm pl-2 py-1 text-orange">
        {t('faceting.when.lastAvailability')}
      </div>
      <div className="text-center text-sm pl-4 py-1 text-gray-light">
        {t('faceting.when.notAvailable')}
      </div>
    </div>
  );
};

export const FilterWhen = ({
  changeDepartureDate,
  changeTripDuration,
  dateFormat,
  defaultDuration = 7,
  departureDate,
  duration,
  isSubmittable,
  label = '',
  navigationByYear,
  openCalendarToDate,
  optionsDuration = [],
  prices = {},
  showDurationLabel = true,
  offsetCalendar,
}: Props) => {
  const from = departureDate ? moment(departureDate).toDate() : null;
  const to =
    from && duration
      ? moment(from)
          .add({ days: duration })
          .toDate()
      : null;
  const [hovered, setHovered] = useState<?Date>(from);

  const computeDisplayedDuration = () => {
    const displayedDuration =
      from && (to || hovered)
        ? dateDiff(from, to || hovered || from) || duration || defaultDuration
        : 0;
    return displayedDuration || defaultDuration || 0;
  };
  const displayedDuration = computeDisplayedDuration();

  useEffect(
    () => {
      if (!departureDate) {
        setHovered(null);
      }
    },
    [departureDate],
  );

  const onDayMouseOver = (day: ?Date) => {
    setHovered(day);
  };

  const onSelect = (dates: { from: ?Date, to: ?Date }) => {
    changeDepartureDate(dates.from);

    if (dates.from && dates.to) {
      const updatedDuration = dateDiff(dates.from, dates.to);
      changeTripDuration(updatedDuration);
    } else {
      changeTripDuration(null);
    }
  };

  const hasPrices = Object.keys(prices || {}).length > 0;

  const cls = [
    'p-2 mx-4 mb-3 bg-white border-1 border-gray-light rounded',
    'lg:p-0 lg:m-1 md:mx-auto lg:rounded-none lg:border-0 lg:mr-1 text-center',
  ];

  const durationLabel = messageFormat({
    n: t('faceting.when.durationPlural'),
    _0: '-',
    _1: t('faceting.when.durationSingular'),
  }).format({
    n: displayedDuration,
    count: displayedDuration,
  });

  return (
    <div data-testid="FilterWhen" className="block p-2 mb-10 sm:m-0">
      {label ? (
        <Fragment>
          {isSubmittable ? (
            <div className="text-sm font-bold text-left text-orange flex items-center">
              <Icon name={ICONS.WARNING} width="2.5em" className="mr-3 flex" />
              <span>{label}</span>
            </div>
          ) : (
            <div className="text-lg font-bold lg:text-center">{label}</div>
          )}
        </Fragment>
      ) : null}
      <div className="block p-2 mx-2 text-base sm:hidden">
        <div className="flex justify-between mb-1">
          <span>{t('faceting.when.arrivalDateTitle')}</span>
          <span className="font-bold text-blue">
            {from ? moment(from).format(dateFormat) : ' -'}
          </span>
        </div>
        <div className="flex justify-between mb-1">
          <span>{t('faceting.when.departureDateTitle')}</span>
          <span className="font-bold text-blue">{to ? moment(to).format(dateFormat) : ' -'}</span>
        </div>
      </div>
      <div className={cls.join(' ')}>
        {showDurationLabel && !hasPrices ? (
          <div className="px-4 py-2 mb-1 lg:px-2 sm:border-b">
            <div className="flex justify-between items-center mt-1 text-base">
              <span>{t('faceting.when.duration')}</span>
              <span data-testid="duration-label">{durationLabel}</span>
            </div>
          </div>
        ) : null}
        {hasPrices ? (
          <div className="w-full sm:border-b border-gray-light py-1 mb-2">
            <div className="text-center text-base">
              {t('faceting.when.pricesBasedOn')}{' '}
              <strong data-testid="prices-duration-label">{durationLabel}</strong>
            </div>
            <PriceLegend />
          </div>
        ) : null}

        <TripDatePicker
          navigationByYear={navigationByYear}
          offsetCalendar={offsetCalendar}
          onSelect={onSelect}
          onDayMouseOver={onDayMouseOver}
          openCalendarToDate={openCalendarToDate}
          optionsDuration={optionsDuration}
          prices={prices}
          selectedDays={{ from, to }}
          theme="compact"
        />
      </div>
    </div>
  );
};
